import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { Box, Text, Row } from '@simplywallst/ui-core';

import { getPath } from '@/router/utils';
import { ROUTE_ARTICLE } from '@/constants/routes';

import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import { useIsMobileScreen } from '@/hooks/useMedia';

import { ImageHeader } from '@components/Header';

import { Author } from './Author';
import { Excerpt } from './Excerpt';

import { TitleContainer } from './styled';

type ArticleSummaryProps = {
  slug: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  publishedDate: string;
  author: string;
  authorAvatarUrl: string;
  authorSlug: string;
};
export const ArticleSummary = ({
  title,
  author,
  authorAvatarUrl,
  authorSlug,
  imageUrl,
  excerpt,
  slug,
  publishedDate,
}: ArticleSummaryProps) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'ArticleSummary',
  });
  const { srcSet: coverImageSrcSet } = getResponsiveImage({
    src: imageUrl,
    breakpoints: [
      { width: 1024, height: 152 },
      { width: 1023, height: 135 },
      { width: 768, height: 135, fit: 'contain' },
      { width: 425, height: 135, fit: 'contain' },
      { width: 375, height: 135, fit: 'contain' },
    ],
  });

  const coverImageSrc = useCloudflareImage({
    src: imageUrl,
    width: 470,
    height: 210,
    fit: 'cover',
  });

  const canonicalUrl = getPath({ type: ROUTE_ARTICLE, payload: { slug } });
  const clickHandler = useCallback(() => {
    trackEvent({
      modifier: 'market-latest-article',
      action: 'click',
      slug,
    });
  }, [slug, trackEvent]);

  return (
    <Track>
      <RouterLink
        noUnderline
        onClick={clickHandler}
        to={{
          type: ROUTE_ARTICLE,
          payload: { slug },
        }}
      >
        <Box
          backgroundColor={isMobileScreen ? 'spaceGray' : 'noir'}
          borderRadius="x1"
          marginBottom="x1"
        >
          <TitleContainer>
            <ImageHeader
              imageSource={coverImageSrc}
              imageSourceSet={coverImageSrcSet}
            >
              <RouterLink
                noUnderline
                to={{
                  type: ROUTE_ARTICLE,
                  payload: { slug },
                }}
                onClick={() => {
                  trackEvent({
                    modifier: 'market-latest-article',
                    action: 'click',
                    slug,
                  });
                }}
                data-cy-id="market-page-title"
              >
                <Text typography="smallStrong">{title}</Text>
              </RouterLink>
              <Box marginBottom="x1" marginTop="x1">
                <Row>
                  <Author
                    canonicalUrl={canonicalUrl}
                    slug={slug}
                    author={author}
                    authorSlug={authorSlug}
                    authorAvatarUrl={authorAvatarUrl}
                    publishedDate={publishedDate}
                  />
                </Row>
              </Box>
            </ImageHeader>
          </TitleContainer>
          <Excerpt excerpt={excerpt} slug={slug} />
        </Box>
      </RouterLink>
    </Track>
  );
};
